import clone from "clone";
import { useMemo } from "react";
import type { ImmutableArray, ImmutableObject } from "@/lib/immutable";
import { storefrontSdk } from "@/storefrontsdk";
import { createStore, type Store, useStore } from "./store";
import {
	type Catalog,
	type CatalogState,
	useCatalogState,
} from "./useCatalogState";
import type { PackageItem, Product } from "./useInventoryState";
import {
	type StorefrontSettingsState,
	useStorefrontSettingsState,
} from "./useStorefrontSettingsState";

export class ShoppingCartItemModel {
	product: ImmutableObject<Product>;
	qty: number;
	packageItemQtys: { [key: string]: number } = {};
	showPackageItems: boolean = false;
	hasQtyError: boolean = false;

	constructor(product: ImmutableObject<Product>, qty: number) {
		this.product = product;
		this.qty = qty;
	}
}

const markProductAsUnavailable = (
	product: ImmutableObject<Product>,
): ImmutableObject<Product> => ({
	...product,
	Availability: {
		TotalIn: 0,
		Total: product.Availability?.Total ?? 0,
	},
	PackageItems: product.PackageItems?.map((packageItem) => ({
		...packageItem,
		Availability: {
			TotalIn: 0,
			Total: packageItem.Availability?.Total ?? 0,
		},
	})),
});

const normalizeCartProduct = (product: Product): Product => ({
	...product,
	Id: String(product.Id ?? ""),
});

const storefront_shoppingcartitems_key = "storefront-shoppingcartitems";
const shoppingcartitemsJson = window.localStorage.getItem(
	"storefront-shoppingcartitems",
);
let initialShoppingCartItems: ShoppingCartItemModel[];
if (shoppingcartitemsJson) {
	initialShoppingCartItems = JSON.parse(shoppingcartitemsJson);
} else {
	initialShoppingCartItems = [];
}
const shoppingCartItemsStateStore = createStore<ShoppingCartItemModel[]>(
	initialShoppingCartItems,
);
const shoppingCartMessagesStore = createStore<ShoppingCartMessage[]>([]);

export class ShoppingCartMessage {
	header: string = "";
	description: string = "";
	messages: string[] = [];
}
export class ShoppingCartItemsState {
	private shoppingCartItemsState: Store<ShoppingCartItemModel[]>;
	private catalogState: CatalogState;
	private shoppingCartMessages: Store<ShoppingCartMessage[]>;
	private storefrontSettings: StorefrontSettingsState;

	constructor(
		shoppingCartItemsState: Store<ShoppingCartItemModel[]>,
		catalogState: CatalogState,
		shoppingCartMessages: Store<ShoppingCartMessage[]>,
		storefrontSettings: StorefrontSettingsState,
	) {
		this.shoppingCartItemsState = shoppingCartItemsState;
		this.catalogState = catalogState;
		this.shoppingCartMessages = shoppingCartMessages;
		this.storefrontSettings = storefrontSettings;
	}

	get(): ImmutableArray<ShoppingCartItemModel> {
		const allItemsInCart = this.shoppingCartItemsState.getState();
		return allItemsInCart;
	}

	getMessages(): ImmutableArray<ShoppingCartMessage> {
		return this.shoppingCartMessages.getState();
	}

	private removeMessagesForItems(items: ShoppingCartItemModel[]) {
		if (items.length === 0) {
			return;
		}

		if (this.shoppingCartItemsState.getState().length === 0) {
			this.shoppingCartMessages.setState([]);
			return;
		}

		const removedItemNames = items
			.map((item) => item.product.ItemName)
			.filter((itemName): itemName is string => Boolean(itemName));
		this.shoppingCartMessages.setState((messages) =>
			messages
				.map((message) => ({
					...message,
					messages: message.messages.filter(
						(itemMessage) =>
							!removedItemNames.some(
								(itemName) =>
									itemMessage === itemName ||
									itemMessage.startsWith(`${itemName} DailyRate `) ||
									itemMessage.startsWith(`${itemName} WeeklyRate `) ||
									itemMessage.startsWith(`${itemName} MonthlyRate `),
							),
					),
				}))
				.filter((message) => message.messages.length > 0),
		);
	}

	reset(locationId: string, warehouseId: string, catalogId?: string) {
		const previousItems = this.shoppingCartItemsState.getState();
		if (!catalogId) {
			// delete all shopping cart items
			const catalogs = this.catalogState.get().catalogs || [];
			if (catalogs) {
				const locationCatalogs =
					this.catalogState
						.get()
						.catalogs?.filter(
							(c) =>
								c.LocationId === locationId && c.WarehouseId === warehouseId,
						) || [];
				const locationCatalogIds: string[] = locationCatalogs.map(
					(item) => item.Id,
				);
				// when it's not a catalog specific checkout, we filter item the other locations from the cart
				const shoppingCartItems = this.shoppingCartItemsState.getState();
				let shoppingCartItemsCopy = clone(shoppingCartItems);
				if (!catalogId) {
					shoppingCartItemsCopy = shoppingCartItemsCopy.filter(
						(item) =>
							locationCatalogIds.indexOf(item.product.WebCatalogId || "") < 0,
					);
					this.shoppingCartItemsState.setState(shoppingCartItemsCopy);
				} else {
					this.shoppingCartItemsState.setState([]);
				}
			}
		} else {
			// delete the shopping cart items from the specified catalog
			this.shoppingCartItemsState.setState((prev) =>
				prev.filter((item) => item.product.WebCatalogId !== catalogId),
			);
		}
		const remainingItems = this.shoppingCartItemsState.getState();
		this.removeMessagesForItems(
			previousItems.filter(
				(item) =>
					!remainingItems.some(
						(remainingItem) =>
							remainingItem.product.Id === item.product.Id &&
							remainingItem.product.WebCatalogId === item.product.WebCatalogId,
					),
			),
		);
		this.saveToLocalStorage();
	}

	addFromProduct(
		product: ImmutableObject<Product>,
		qty: number | string,
	): ShoppingCartItemModel {
		if (typeof qty === "string") {
			qty = parseFloat(qty);
		}
		const item = new ShoppingCartItemModel(product, qty);
		this.add(item);
		return item;
	}

	add(item: ShoppingCartItemModel) {
		let alreadyInCart = false;
		for (let i = 0; i < this.shoppingCartItemsState.getState().length; i++) {
			const item2 = this.shoppingCartItemsState.getState()[i];
			if (item.product.Id === item2.product.Id) {
				alreadyInCart = true;
				this.changeQty(i, item, item2.qty + item.qty);
			}
		}
		if (!alreadyInCart) {
			this.shoppingCartItemsState.setState((prev) => [...prev, item]);
			this.saveToLocalStorage();
		}
	}

	replace(items: ShoppingCartItemModel[]) {
		this.shoppingCartItemsState.setState(items);
		this.saveToLocalStorage();
	}

	delete(item: ShoppingCartItemModel) {
		const shoppingCartItems = this.shoppingCartItemsState.getState();
		if (shoppingCartItems) {
			const index = shoppingCartItems.findIndex(
				(o) => o.product.Id === item.product.Id,
			);
			if (index > -1) {
				this.shoppingCartItemsState.setState((prev) => {
					const next = [...prev];
					next.splice(index, 1);
					return next;
				});
				this.removeMessagesForItems([shoppingCartItems[index]]);
				this.saveToLocalStorage();
			}
		}
	}

	deleteItems(items: ShoppingCartItemModel[]) {
		const shoppingCartItems = this.shoppingCartItemsState.getState();
		const indexesToDelete: number[] = [];
		if (shoppingCartItems && items) {
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				const index = shoppingCartItems.findIndex(
					(o) => o.product.Id === item.product.Id,
				);
				if (index > -1) {
					indexesToDelete.push(index);
				}
			}
			if (indexesToDelete.length > 0) {
				const deletedItems = shoppingCartItems.filter((_item, index) =>
					indexesToDelete.includes(index),
				);
				this.shoppingCartItemsState.setState((prev) =>
					prev.filter((_item, index) => !indexesToDelete.includes(index)),
				);
				this.removeMessagesForItems(deletedItems);
				this.saveToLocalStorage();
			}
		}
	}

	deletePackageItem(
		shoppingCartItem: ShoppingCartItemModel,
		packageItem: ImmutableObject<PackageItem>,
	) {
		this.setPackageItemQty(shoppingCartItem.product, packageItem, 0);
	}

	changeQty(
		index: number,
		item: ImmutableObject<ShoppingCartItemModel>,
		qty: number,
	) {
		const shoppingCartItems = JSON.parse(
			JSON.stringify(this.shoppingCartItemsState.getState()),
		) as ShoppingCartItemModel[];
		if (index > -1) {
			if (qty > 0) {
				shoppingCartItems[index].qty = qty;
				this.shoppingCartItemsState.setState(shoppingCartItems);
				this.saveToLocalStorage();
			} else {
				this.delete(item);
			}
		}
	}

	getQty(product: ImmutableObject<Product>): number {
		const shoppingCartItems = this.shoppingCartItemsState.getState();
		for (let i = 0; i < shoppingCartItems.length; i++) {
			const item = shoppingCartItems[i];
			if (item.product.Id === product.Id) {
				return item.qty;
			}
		}
		return 0;
	}

	setQty(product: ImmutableObject<Product>, qty: number): void {
		const productId = product.Id;
		const shoppingCartItems = JSON.parse(
			JSON.stringify(this.shoppingCartItemsState.getState()),
		) as ShoppingCartItemModel[];
		const shoppingCartItemIndex = shoppingCartItems.findIndex(
			(i) => i.product.Id === product.Id,
		);
		if (
			shoppingCartItemIndex !== -1 &&
			shoppingCartItemIndex >= 0 &&
			shoppingCartItemIndex < shoppingCartItems.length
		) {
			const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
			if (qty > 0) {
				shoppingCartItem.qty = qty;
				this.shoppingCartItemsState.setState(shoppingCartItems);
			} else {
				this.shoppingCartItemsState.setState((prev) => {
					const next = [...prev];
					next.splice(shoppingCartItemIndex, 1);
					return next;
				});
				this.removeMessagesForItems([shoppingCartItem]);
			}
			this.saveToLocalStorage();
		} else {
			this.addFromProduct(product, qty);
		}

		// product becomes undefined by this point (or at least gives an undefined exception)
		const shoppingCartItems2 = JSON.parse(
			JSON.stringify(this.shoppingCartItemsState.getState()),
		) as ShoppingCartItemModel[];
		const shoppingCartItem2 = shoppingCartItems2.find(
			(i) => i.product.Id === productId,
		);
		if (shoppingCartItem2) {
			const packageItems = shoppingCartItem2.product.PackageItems ?? [];
			for (let i = 0; i < packageItems.length; i++) {
				const packageItem = packageItems[i];
				if (packageItem) {
					if (
						shoppingCartItem2.packageItemQtys[packageItem.InventoryId] ===
						undefined
					) {
						shoppingCartItem2.packageItemQtys[packageItem.InventoryId] = 0;
					}
					if (qty > 0) {
						if (!packageItem.IsOption) {
							const piDefaultQuantity = packageItem.DefaultQuantity;
							if (
								packageItem.DefaultQuantity < 1 &&
								packageItem.DefaultQuantity > 0
							) {
								shoppingCartItem2.packageItemQtys[packageItem.InventoryId] =
									Math.ceil(qty * piDefaultQuantity);
							} else {
								shoppingCartItem2.packageItemQtys[packageItem.InventoryId] =
									qty * piDefaultQuantity;
							}
							this.shoppingCartItemsState.setState(shoppingCartItems2);
						}
					} else {
						this.shoppingCartItemsState.setState((prev) => {
							const next = [...prev];
							next.splice(i, 1);
							return next;
						});
					}
				}
			}
			this.saveToLocalStorage();
		}
	}

	getPackageItemQty(
		product: ImmutableObject<Product>,
		packageiItem: ImmutableObject<PackageItem>,
	): number {
		const shoppingCartItems = this.shoppingCartItemsState.getState();
		const shoppingCartItem = shoppingCartItems.find(
			(i) => i.product.Id === product.Id,
		);
		if (shoppingCartItem) {
			if (shoppingCartItem.packageItemQtys?.[packageiItem.InventoryId]) {
				return shoppingCartItem.packageItemQtys[packageiItem.InventoryId];
			}
		}
		return 0;
	}

	setPackageItemQty(
		product: ImmutableObject<Product>,
		packageItem: ImmutableObject<PackageItem>,
		qty: number,
	): void {
		let shoppingCartItems = JSON.parse(
			JSON.stringify(this.shoppingCartItemsState.getState()),
		) as ShoppingCartItemModel[];
		let shoppingCartItemIndex = shoppingCartItems.findIndex(
			(i) => i.product.Id === product.Id,
		);
		if (shoppingCartItemIndex === -1) {
			this.setQty(product, qty);
			shoppingCartItems = JSON.parse(
				JSON.stringify(this.shoppingCartItemsState.getState()),
			) as ShoppingCartItemModel[];
			shoppingCartItemIndex = shoppingCartItems.findIndex(
				(i) => i.product.Id === product.Id,
			);
		}
		// if we were able to find the main package in the shopping cart, then update the count of the packageitem
		if (
			shoppingCartItemIndex !== -1 &&
			shoppingCartItemIndex >= 0 &&
			shoppingCartItemIndex < shoppingCartItems.length
		) {
			const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
			if (shoppingCartItem) {
				shoppingCartItem.packageItemQtys[packageItem.InventoryId] = qty;
				this.shoppingCartItemsState.setState(shoppingCartItems);
				this.saveToLocalStorage();
				return;
			}
		}
	}

	// returns the total qty of the I-Code in the shopping across across all products and package items
	getICodeQtyInCart(icode: string) {
		let qtyInCartFromOtherItems = 0;
		const shoppingCartItems = this.shoppingCartItemsState.getState();
		for (const item of shoppingCartItems) {
			if (item.product.InventoryNo === icode) {
				qtyInCartFromOtherItems += item.qty;
			}
			for (const packageItem of item.product.PackageItems ?? []) {
				if (packageItem.ICode === icode) {
					qtyInCartFromOtherItems +=
						item.packageItemQtys[packageItem.InventoryId] ?? 0;
				}
			}
		}
		return qtyInCartFromOtherItems;
	}

	getMaxProductQty(product: Product | ImmutableObject<Product>) {
		let maxQty = 0;
		if (product.Availability !== undefined) {
			let icodeQtyInCart = 0;
			let thisProductQtyInCart = 0;
			if (
				(this.storefrontSettings.get().settings
					?.SeparateQuoteRequestsByCatalog ?? false) === false
			) {
				icodeQtyInCart = this.getICodeQtyInCart(product.InventoryNo);
				thisProductQtyInCart = this.getQty(product);
			}
			maxQty =
				product.Availability.TotalIn - icodeQtyInCart + thisProductQtyInCart;
		}
		return maxQty;
	}

	getMaxPackageItemQty(
		product: Product | ImmutableObject<Product>,
		packageItem: PackageItem | ImmutableObject<PackageItem>,
	) {
		let maxQty = 0;
		if (packageItem.Availability !== undefined) {
			let icodeQtyInCart = 0;
			let thisPackageQtyInCart = 0;
			if (
				(this.storefrontSettings.get().settings
					?.SeparateQuoteRequestsByCatalog ?? false) === false
			) {
				icodeQtyInCart = this.getICodeQtyInCart(packageItem.ICode);
				thisPackageQtyInCart = this.getPackageItemQty(product, packageItem);
			}
			maxQty =
				packageItem.Availability.TotalIn -
				icodeQtyInCart +
				thisPackageQtyInCart;
		}
		return maxQty;
	}

	setShowPackageItems(
		shoppingCartItem: ShoppingCartItemModel,
		showPackageItems: boolean,
	) {
		const shoppingCartItems = JSON.parse(
			JSON.stringify(this.shoppingCartItemsState.getState()),
		) as ShoppingCartItemModel[];
		const shoppingCartItemIndex = shoppingCartItems.findIndex(
			(i) => i.product.Id === shoppingCartItem.product.Id,
		);
		if (shoppingCartItemIndex !== -1) {
			const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
			if (shoppingCartItem) {
				shoppingCartItem.showPackageItems = showPackageItems;
				this.shoppingCartItemsState.setState(shoppingCartItems);
				this.saveToLocalStorage();
			}
		}
	}

	getRates(shoppingCartItem: ShoppingCartItemModel) {
		let dailyRate = shoppingCartItem.product.DailyRate;
		let weeklyRate = shoppingCartItem.product.WeeklyRate;
		let monthlyRate = shoppingCartItem.product.MonthlyRate;
		let salesPrice = shoppingCartItem.product.SalesPrice;
		if (
			shoppingCartItem.packageItemQtys !== undefined &&
			Object.keys(shoppingCartItem.packageItemQtys).length > 0
		) {
			dailyRate = 0;
			weeklyRate = 0;
			monthlyRate = 0;
			for (const inventoryId in shoppingCartItem.packageItemQtys) {
				if (shoppingCartItem.product.PackageItems) {
					const packageItem = shoppingCartItem.product.PackageItems.find(
						(pi) => pi.InventoryId === inventoryId,
					);
					const qty = shoppingCartItem.packageItemQtys[inventoryId];
					if (
						packageItem !== undefined &&
						qty !== undefined /*&& (packageItem.IsOption)*/
					) {
						if (!packageItem.IsOption) {
							dailyRate += packageItem.DailyExtended * qty;
							weeklyRate += packageItem.WeeklyExtended * qty;
							monthlyRate += packageItem.MonthlyExtended * qty;
						} else {
							dailyRate += packageItem.DailyRate * qty;
							weeklyRate += packageItem.WeeklyRate * qty;
							monthlyRate += packageItem.MonthlyRate * qty;
						}
						salesPrice += packageItem.SalesPrice * qty;
					}
				}
			}
		}
		return {
			dailyRate: dailyRate,
			weeklyRate: weeklyRate,
			monthlyRate: monthlyRate,
			salesPrice: salesPrice,
		};
	}

	getRatesByProduct(product?: ImmutableObject<Product>) {
		let rates = {
			dailyRate: 0,
			weeklyRate: 0,
			monthlyRate: 0,
			salesPrice: 0,
		};
		if (product) {
			rates = {
				dailyRate: product.DailyRate,
				weeklyRate: product.WeeklyRate,
				monthlyRate: product.MonthlyRate,
				salesPrice: product.SalesPrice,
			};
			const shoppingCartItems = JSON.parse(
				JSON.stringify(this.shoppingCartItemsState.getState()),
			) as ShoppingCartItemModel[];
			const shoppingCartItemIndex = shoppingCartItems.findIndex(
				(i) => i.product.Id === product.Id,
			);
			if (shoppingCartItemIndex !== -1) {
				const shoppingCartItem = shoppingCartItems[shoppingCartItemIndex];
				rates = this.getRates(shoppingCartItem);
			}
		}
		return rates;
	}

	private saveToLocalStorage() {
		const obj = this.shoppingCartItemsState.getState();
		let json = "";
		if (obj) {
			json = JSON.stringify(obj);
		}
		window.localStorage.setItem(storefront_shoppingcartitems_key, json);
	}

	addMessage(message: ShoppingCartMessage) {
		this.shoppingCartMessages.setState((prev) => [...prev, message]);
	}

	async reloadProductsByIdAsync(
		productids: string,
		warehouseid: string,
		locationid: string,
		fromDate: string,
		toDate: string,
	) {
		const productidsList = productids.split(",");
		// load all the products with auto-paging
		const products = await new Promise<Product[]>(async (resolve, reject) => {
			try {
				let more = true;
				const allProducts: Product[] = [];
				const pageSize = 100;
				let pageno = 1;
				while (more) {
					const productsResponse = (
						await storefrontSdk.storefrontV1GetWebCatalogProductsWithAvailabilityAsync(
							productids,
							locationid,
							warehouseid,
							fromDate,
							toDate,
							pageno,
							pageSize,
						)
					).data as unknown as { Items?: Product[]; TotalItems?: number };
					allProducts.push(...(productsResponse.Items ?? []));
					if ((productsResponse.TotalItems ?? 0) === allProducts.length) {
						more = false;
					}
					pageno++;
				}
				resolve(allProducts);
			} catch (error) {
				reject(error);
			}
		});

		// copy the shopping cart
		const shoppingCartItemsCopy = JSON.parse(
			JSON.stringify(this.shoppingCartItemsState.getState()),
		) as ShoppingCartItemModel[];

		// show a message on any products that are no longer in the catalog or unavailable
		const notInCatalogMessage: ShoppingCartMessage = {
			header:
				"The following items are no longer available in the catalog or at the selected location.",
			description: "Please remove the items from your shopping cart.",
			messages: [],
		};
		const priceChangedMessage: ShoppingCartMessage = {
			header: "The following items have changed price.",
			description:
				"Items in your shopping cart are updated to reflect the most current price from the product details page.",
			messages: [],
		};
		const unavailableMessage: ShoppingCartMessage = {
			header:
				"The following items are not available during the requested period.",
			description: "",
			messages: [],
		};
		for (let i = 0; i < shoppingCartItemsCopy.length; i++) {
			const shoppingCartItem = shoppingCartItemsCopy[i];
			if (productidsList.indexOf(shoppingCartItem.product.Id) >= 0) {
				shoppingCartItem.hasQtyError = false;
				const product = products.find(
					(p) => String(p.Id) === String(shoppingCartItem.product.Id),
				);
				if (product === undefined) {
					notInCatalogMessage.messages.push(
						`${shoppingCartItem.product.ItemName}`,
					);
					shoppingCartItem.hasQtyError = true;
					shoppingCartItem.product = markProductAsUnavailable(
						shoppingCartItem.product,
					);
				} else {
					await this.catalogState.loadAsync();
					const catalog = this.catalogState.getProductCatalog(product);
					const allowAddingUnavailableItems =
						catalog?.AllowAddingUnavailableItems ?? false;
					const totalIn = product.Availability?.TotalIn ?? 0;
					if (
						!allowAddingUnavailableItems &&
						product.Availability !== undefined &&
						totalIn <= 0
					) {
						unavailableMessage.messages.push(
							`${shoppingCartItem.product.ItemName}`,
						);
						shoppingCartItem.hasQtyError = true;
					}
					if (product.DailyRate !== shoppingCartItem.product.DailyRate) {
						priceChangedMessage.messages.push(
							`${shoppingCartItem.product.ItemName} DailyRate for ${product.DailyRate > shoppingCartItem.product.DailyRate ? "increased" : "decreased"} from ${shoppingCartItem.product.CurrencySymbol} ${shoppingCartItem.product.DailyRate} to ${product.CurrencySymbol} ${product.DailyRate}`,
						);
					}
					if (product.WeeklyRate !== shoppingCartItem.product.WeeklyRate) {
						priceChangedMessage.messages.push(
							`${shoppingCartItem.product.ItemName} WeeklyRate for ${product.WeeklyRate > shoppingCartItem.product.WeeklyRate ? "increased" : "decreased"} from ${shoppingCartItem.product.CurrencySymbol} ${shoppingCartItem.product.WeeklyRate} to ${product.CurrencySymbol} ${product.WeeklyRate}`,
						);
					}
					if (product.MonthlyRate !== shoppingCartItem.product.MonthlyRate) {
						priceChangedMessage.messages.push(
							`${shoppingCartItem.product.ItemName} MonthlyRate for ${product.MonthlyRate > shoppingCartItem.product.MonthlyRate ? "increased" : "decreased"} from ${shoppingCartItem.product.MonthlyRate} ${shoppingCartItem.product.MonthlyRate} to ${product.CurrencySymbol} ${product.MonthlyRate}`,
						);
					}
				}
			}
		}
		this.shoppingCartMessages.setState([]);
		if (notInCatalogMessage.messages.length > 0) {
			this.shoppingCartMessages.setState((prev) => [
				...prev,
				notInCatalogMessage,
			]);
		}
		if (unavailableMessage.messages.length > 0) {
			this.shoppingCartMessages.setState((prev) => [
				...prev,
				unavailableMessage,
			]);
		}
		if (priceChangedMessage.messages.length > 0) {
			this.shoppingCartMessages.setState((prev) => [
				...prev,
				priceChangedMessage,
			]);
		}

		// update the product for shopping cart items.
		for (let i = 0; i < products.length; i++) {
			const product = products[i];
			const shoppingCartItem = shoppingCartItemsCopy.find(
				(c) => String(c.product.Id) === String(product.Id),
			);
			if (shoppingCartItem) {
				shoppingCartItem.product = normalizeCartProduct(product);
			}
		}

		this.shoppingCartItemsState.setState(shoppingCartItemsCopy);
		this.saveToLocalStorage();
	}

	/**
	 * Reload the product info that's saved in localStorage on the shopppingCart items, validate that the user is allowed to have the items in their cart,
	 * and set reactive state for the shopping cart messages.
	 */
	async reloadProductsAsync(
		warehouseid: string,
		locationid: string,
		fromDate: string,
		toDate: string,
	) {
		const isUniversity =
			this.storefrontSettings.get().settings?.IsUniversity ?? false;

		// reset all shopping cart qty errors
		//const items = clone<ShoppingCartItemModel[]>(this.shoppingCartItemsState.get() as any);
		//for (let i = 0; i < items.length; i++) {
		//    let item = items[i];
		//    items[i] = {
		//        ...items[i],
		//        hasQtyError: false,
		//    }
		//    this.shoppingCartItemsState.set(items);
		//}

		if (isUniversity) {
			// build a list of catalogs so we we can call the products endpoint once for each catalog that's in the shopping cart.
			const catalogs: Catalog[] = [];
			const items = this.shoppingCartItemsState.getState();
			for (const item of items) {
				const webCatalogId = item.product.WebCatalogId;
				const existingCatalog = catalogs?.find((c) => c?.Id === webCatalogId);
				if (!existingCatalog) {
					const catalog = this.catalogState.getCatalogById(webCatalogId);
					if (catalog) {
						catalogs.push(catalog as Catalog);
					}
				}
			}
			for (const catalog of catalogs) {
				let productids: string = "";
				const shoppingCartItems = this.get();
				for (var shoppingCartItem of shoppingCartItems) {
					if (shoppingCartItem.product.WebCatalogId === catalog.Id) {
						if (productids.length > 0) {
							productids += ",";
						}
						productids += shoppingCartItem.product.Id;
					}
				}
				if (productids) {
					await this.reloadProductsByIdAsync(
						productids,
						catalog.WarehouseId,
						catalog.LocationId,
						fromDate,
						toDate,
					);
				}
			}
		} else if (warehouseid && locationid) {
			let productids: string = "";
			const shoppingCartItems = this.get();
			for (var shoppingCartItem2 of shoppingCartItems) {
				if (productids.length > 0) {
					productids += ",";
				}
				productids += shoppingCartItem2.product.Id;
			}
			if (productids) {
				await this.reloadProductsByIdAsync(
					productids,
					warehouseid,
					locationid,
					fromDate,
					toDate,
				);
			}
		}
	}

	hasCatalogTypes() {
		const shoppingCartItems = this.shoppingCartItemsState.getState();
		let hasRental = false;
		let hasSales = false;
		for (let i = 0; i < shoppingCartItems.length; i++) {
			const shoppingCartItem = shoppingCartItems[i];
			const catalog = this.catalogState.getCatalogById(
				shoppingCartItem.product.WebCatalogId,
			);
			if (catalog?.HasRental) {
				hasRental = true;
			}
			if (catalog?.HasSales) {
				hasSales = true;
			}
		}
		return {
			hasRental,
			hasSales,
		};
	}
}

export function useShoppingCartItemsState(): ShoppingCartItemsState {
	const catalogState = useCatalogState();
	const storefrontSettings = useStorefrontSettingsState();
	useStore(shoppingCartItemsStateStore);
	useStore(shoppingCartMessagesStore);
	return useMemo(
		() =>
			new ShoppingCartItemsState(
				shoppingCartItemsStateStore,
				catalogState,
				shoppingCartMessagesStore,
				storefrontSettings,
			),
		[catalogState, storefrontSettings],
	);
}
